import React from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { FaStar, FaCalendarAlt, FaTag, FaCar } from 'react-icons/fa';

const Container = styled.div`
  padding: ${theme.spacing.lg};
`;

const Header = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%);
  color: ${theme.colors.white};
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  margin-bottom: ${theme.spacing.xl};
  text-align: center;
`;

const WelcomeText = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: ${theme.spacing.sm};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  opacity: 0.9;
`;

const Section = styled.section`
  margin-bottom: ${theme.spacing.xl};
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${theme.colors.secondary};
  margin-bottom: ${theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const Card = styled.div`
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.md};
  margin-bottom: ${theme.spacing.md};
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const OfferCard = styled(Card)`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, #ef4444 100%);
  color: ${theme.colors.white};
  position: relative;
  overflow: hidden;
`;

const OfferBadge = styled.div`
  position: absolute;
  top: ${theme.spacing.md};
  right: ${theme.spacing.md};
  background: ${theme.colors.white};
  color: ${theme.colors.primary};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-weight: bold;
  font-size: 0.9rem;
`;

const OfferTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: bold;
  margin-bottom: ${theme.spacing.sm};
`;

const OfferDescription = styled.p`
  opacity: 0.9;
  margin-bottom: ${theme.spacing.md};
`;

const OfferPrice = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: ${theme.spacing.sm};
`;

const OfferButton = styled.button`
  background: ${theme.colors.white};
  color: ${theme.colors.primary};
  border: none;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${theme.colors.lightGray};
    transform: translateY(-2px);
  }
`;

const EventCard = styled(Card)`
  border-left: 4px solid ${theme.colors.primary};
`;

const EventHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing.md};
`;

const EventTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: bold;
  color: ${theme.colors.secondary};
  margin-bottom: ${theme.spacing.xs};
`;

const EventDate = styled.div`
  color: ${theme.colors.primary};
  font-weight: 600;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

const EventDescription = styled.p`
  color: ${theme.colors.mediumGray};
  line-height: 1.5;
`;

const QuickActions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.xl};
`;

const QuickActionCard = styled(Card)`
  text-align: center;
  cursor: pointer;
  padding: ${theme.spacing.lg};
`;

const QuickActionIcon = styled.div`
  font-size: 2rem;
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.md};
`;

const QuickActionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${theme.colors.secondary};
  margin-bottom: ${theme.spacing.sm};
`;

const QuickActionDescription = styled.p`
  font-size: 0.9rem;
  color: ${theme.colors.mediumGray};
`;

const HomePage: React.FC = () => {
  const offers = [
    {
      id: 1,
      title: "Premium Wash Package",
      description: "Complete exterior and interior cleaning with waxing",
      price: "AED 150",
      originalPrice: "AED 200",
      discount: "25% OFF"
    },
    {
      id: 2,
      title: "Monthly Subscription",
      description: "Unlimited washes for the entire month",
      price: "AED 299",
      originalPrice: "AED 399",
      discount: "30% OFF"
    }
  ];

  const events = [
    {
      id: 1,
      title: "Car Enthusiasts Meetup",
      date: "March 15, 2024",
      description: "Join us for an exciting meetup with fellow car lovers. Show off your ride and win prizes!"
    },
    {
      id: 2,
      title: "Detailing Workshop",
      date: "March 22, 2024",
      description: "Learn professional car detailing techniques from our experts."
    }
  ];

  const quickActions = [
    {
      title: "Book Service",
      description: "Schedule your car wash",
      icon: FaCar
    },
    {
      title: "View Events",
      description: "Check upcoming events",
      icon: FaCalendarAlt
    },
    {
      title: "Special Offers",
      description: "Browse current deals",
      icon: FaTag
    }
  ];

  return (
    <Container>
      <Header>
        <WelcomeText>Welcome to 971 Run</WelcomeText>
        <Subtitle>Your premium car care destination</Subtitle>
      </Header>

      <Section>
        <SectionTitle>
          <FaTag />
          Special Offers
        </SectionTitle>
        {offers.map((offer) => (
          <OfferCard key={offer.id}>
            <OfferBadge>{offer.discount}</OfferBadge>
            <OfferTitle>{offer.title}</OfferTitle>
            <OfferDescription>{offer.description}</OfferDescription>
            <OfferPrice>
              {offer.price} <span style={{ textDecoration: 'line-through', opacity: 0.7, fontSize: '1rem' }}>
                {offer.originalPrice}
              </span>
            </OfferPrice>
            <OfferButton>Book Now</OfferButton>
          </OfferCard>
        ))}
      </Section>

      <Section>
        <SectionTitle>
          <FaCalendarAlt />
          Upcoming Events
        </SectionTitle>
        {events.map((event) => (
          <EventCard key={event.id}>
            <EventHeader>
              <div>
                <EventTitle>{event.title}</EventTitle>
                <EventDate>
                  <FaCalendarAlt />
                  {event.date}
                </EventDate>
              </div>
            </EventHeader>
            <EventDescription>{event.description}</EventDescription>
          </EventCard>
        ))}
      </Section>

      <Section>
        <SectionTitle>
          <FaStar />
          Quick Actions
        </SectionTitle>
        <QuickActions>
          {quickActions.map((action, index) => {
            const IconComponent = action.icon;
            return (
              <QuickActionCard key={index}>
                <QuickActionIcon>
                  <IconComponent />
                </QuickActionIcon>
                <QuickActionTitle>{action.title}</QuickActionTitle>
                <QuickActionDescription>{action.description}</QuickActionDescription>
              </QuickActionCard>
            );
          })}
        </QuickActions>
      </Section>
    </Container>
  );
};

export default HomePage;


